
import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'
import { State } from 'vuex-class'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { RefundsOrderBy, Refund, RefundStatus } from '@/interfaces/refund'
import { showErrors, showNotification } from '@/utils/snack'
import { utcDateTimeToLocal } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppRefreshAndExport from '@/components/AppRefreshAndExport.vue'
import AppSearchBtn from '@/components/AppSearchBtn.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleDistrictSelector from '@/components/AppSimpleDistrictSelector.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import ListPage from '@/mixins/list-page'
import { RefundStatusText, statusCodes, statusOptions } from '@/helpers/refund'
import { refundKey, refundOptions, RefundWayText } from '@/helpers/order'
import { exportRefundsList, findRefunds } from './gql'
import { TypedGqlError } from '@/interfaces/error'
import { findDealers } from '@/views/DealerList/gql'
import { auditStatusText } from '@/helpers/audit'
import { formatCurrency } from '@/utils/currency'
import AppSimpleSearchSelector from '@/components/AppSimpleSearchSelector.vue'
import { SnackType } from '@/interfaces/snack'
import { downloadBase64 } from '@/helpers/base64-to-file'
import { dispatchUpdateIfNewOrders } from '../../store/dispatches'

@Component({
  components: {
    AppBreadcrumbs,
    AppRefreshAndExport,
    AppDialog,
    AppSimpleSelector,
    AppSimpleTextField,
    AppSearchBtn,
    AppSimpleDistrictSelector,
    AppSimpleDatePicker,
    AppSimpleSearchSelector
  }
})
export default class RefundList extends mixins(ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: '订单管理'
    },
    {
      text: '退款单'
    }]

  utcDateTimeToLocal = utcDateTimeToLocal
  formatCurrency = formatCurrency
  isGlobal = false
  options = {
    sortBy: ['createdAt'],
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true]
  }

  filter: any = {
    refundTransactionNumber: '',
    refundNumber: '',
    refundWay_EQ: null,
    dealerId_OF_order: {
      id: null,
      label: ''
    }
  }

  totalNum = 0
  refundLoading = true
  disableConfirm = false
  detailInfo: Refund | null = null
  refundDetailDialog = {
    show: false,
    title: '退款单详情',
    disable: false,
    confirmText: '关闭',
    showCancel: false
  }

  headers = [
    { text: '退款方式', value: 'refundWay', sortable: false, width: 140 },
    { text: '审核状态', value: 'auditStatus', sortable: false, width: 100 },
    { text: '经销商', value: 'order.dealer.realname', sortable: false, width: 80 },
    { text: '退款编号', value: 'refundNumber', sortable: false },
    {
      text: '微信退款流水号',
      value: 'transactionNumber'
    },
    { text: '退款状态', value: 'status', width: 120 },
    { text: '申请时间', value: 'createdAt', width: 160 },
    { text: '退款时间', value: 'successTime', sortable: false, width: 160 },
    { text: '订单编号', value: 'order.orderNumber', sortable: false },
    {
      text: '订单流水号',
      value: 'order.transactionNumber',
      sortable: false
    },
    { text: '支付用户手机号', value: 'order.address.phone', sortable: false, width: 140 },
    { text: '学生姓名', value: 'order.address.realname', sortable: false, width: 120 },
    { text: '学校', value: 'order.address.school.name', sortable: false, width: 120 },
    { text: '退款金额', value: 'totalAmount' },
    { text: '订单金额', value: 'totalPrice' }
  ]

  refundList: Refund[] = []

  windowSize = {
    w: 0,
    h: 0
  }

  actionsHeight = 51

  statusOptions = statusOptions()
  statusCodes = statusCodes()
  RefundStatusText = RefundStatusText

  // 退款方式
  refundWayOptions = refundOptions()
  refundKey = refundKey()
  RefundWayText = RefundWayText

  // 审核状态
  AuditStatusText = auditStatusText

  dealerOptions: any[] = []

  @State(state => state.page.RefundList) cache!: string
  mounted () {
    this.onResize()
    this.restorePage(this.cache)
    this.getRefundList()
    this.getDealerOptions()
  }

  @Watch('options', { deep: true })
  onOptionsChanged () {
    this.getRefundList()
  }

  usingStatus (status: string) {
    return {
      'u-tag-green': status === RefundStatus.SUCCESS,
      'u-tag-red': status === RefundStatus.CHANGE,
      'u-tag-orange': status === RefundStatus.PROCESSING,
      'u-tag-dark': status === RefundStatus.REFUNDCLOSE,
      'u-tag-blue': status === RefundStatus.TO_BE_REFUND
    }
  }

  // 获取经销商option
  async getDealerOptions (query?: { take: number; model: string}) {
    const where = query && query.model ? { realname: query.model } : null
    const take = query && query.take ? query.take : 20
    try {
      const result = await this.$apollo.query({
        query: findDealers,
        variables: {
          order: null,
          paging: {
            skip: 0,
            take
          },
          where
        }
      })
      if (result.data.findDealers) {
        this.dealerOptions = result.data.findDealers.dealers.map((option: any) => {
          return {
            id: option.id,
            label: option.realname
          }
        })
      }
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  async getRefundList () {
    const { sortBy, page, itemsPerPage, sortDesc } = this.options
    const {
      refundNumber,
      refundTransactionNumber,
      refundWay_EQ,
      dealerId_OF_order
    } = this.filter
    const by = RefundsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.refundLoading = true
    const order = { by, asc }
    const paging = {
      skip: itemsPerPage * (page - 1),
      take: itemsPerPage
    }
    const where = {
      refundNumber: refundNumber || null,
      transactionNumber: refundTransactionNumber || null,
      refundWay_EQ: this.refundKey[refundWay_EQ] || null,
      dealerId_OF_order: dealerId_OF_order ? dealerId_OF_order.id : null
    }
    try {
      const result = await this.$apollo.query({
        query: findRefunds,
        variables: {
          order,
          paging,
          where
        }
      })
      if (result) {
        this.refundList = result.data.findRefunds.refunds
        this.totalNum = result.data.findRefunds.paging.totalCount
        this.storePage()
      }
      this.refundLoading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }

    dispatchUpdateIfNewOrders({
      refund: false
    })
  }

  async filterQuerySubmit () {
    await this.getRefundList()
  }

  detailHandler (item: Refund) {
    this.detailInfo = item
    this.refundDetailDialog.show = true
  }

  refundFormCancel () {
    this.refundDetailDialog.show = false
  }

  refreshList () {
    this.filter = {}
    this.getRefundList()
  }

  async exportHandler () {
    showNotification({
      msg: '即将开始下载...',
      type: SnackType.Info
    })
    const { sortBy, sortDesc } = this.options
    const {
      refundNumber,
      refundTransactionNumber,
      refundWay_EQ,
      dealerId_OF_order
    } = this.filter
    const by = RefundsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.refundLoading = true
    const order = { by, asc }
    const where = {
      refundNumber: refundNumber || null,
      transactionNumber: refundTransactionNumber || null,
      refundWay_EQ: this.refundKey[refundWay_EQ] || null,
      dealerId_OF_order: dealerId_OF_order ? dealerId_OF_order.id : null
    }
    try {
      const result = await this.$apollo.query({
        query: exportRefundsList,
        variables: {
          order,
          where
        }
      })
      if (result.data) {
        const { fileName, contentB64 } = result.data.exportRefundsList
        downloadBase64(contentB64, fileName)
      }
      this.refundLoading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
    const actionDom = this.$refs.actions as Vue & {
      clientHeight: number;
    }
    this.actionsHeight = actionDom.clientHeight
  }
}
